/**
 * Browser / Tauri connectivity → TanStack `onlineManager`, and the replay that
 * follows reconnect.
 *
 * TanStack's default listener already watches `window` online/offline, but two
 * things make an explicit bridge worth having. First, `navigator.onLine` is
 * only ever a lower bound — it reports "online" for a machine attached to a
 * captive portal or a VPN that is up but routing nowhere — so a failed request
 * has to be able to demote us. Second, the desktop shell is a WebView whose
 * online/offline events fire inconsistently when the host machine sleeps and
 * wakes, which is exactly the laptop-in-a-bag case this feature is for; polling
 * on focus and visibility covers what the events miss.
 */
import type { QueryClient } from '@tanstack/react-query'
import { getCurrentClientPortalContext } from '@/lib/api/client-portal-context'
import { captureRequestScope } from '@/lib/api/request-scope'
import { getWorkspaceSessionSnapshot } from '@/lib/session/workspace-session'
import { selectPendingThreadComments } from '@/modules/threads/comment-commands.api'
import { selectThreadCreationEntries } from '@/modules/threads/creation-commands.api'
import { portalThreadSummaryKey } from '@/modules/threads/portal-thread-owner'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import { hashKey, onlineManager } from '@tanstack/react-query'

import { offlineLedger, restoreOfflineLedger } from './ledger'
import {
  registerOfflineMutationDefaults,
  selectedCommentOwner,
} from './mutation-defaults'
import {
  OFFLINE_MUTATION_KEYS,
  selectOwnerWrites,
  selectPendingWrites,
} from './offline-queue'
import { createOwnedResume } from './owned-work'
import { retryOfflineWrite } from './queue-actions'
import { selectUnsentWorkspaceWrites } from './recovery'

function readBrowserOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

const resumers = new WeakMap<QueryClient, () => Promise<void>>()

function replayCandidates(ownerId: string, queryClient: QueryClient) {
  const entries = offlineLedger.list()
  const unsent = selectUnsentWorkspaceWrites(
    entries,
    ownerId,
    useOrganizationStore.getState().currentOrganization?.id ?? null,
  ).filter(
    entry =>
      entry.envelope.kind !== 'thread-comment.create' &&
      entry.envelope.kind !== 'thread.create',
  )
  const comments = selectPendingThreadComments(
    entries,
    selectedCommentOwner(queryClient),
  ).filter(
    entry =>
      entry.envelope.ownerId === ownerId &&
      (entry.status === 'queued' || entry.status === 'inflight'),
  )
  const creations = selectThreadCreationEntries(
    entries,
    selectedCommentOwner(queryClient),
  ).filter(
    entry =>
      entry.envelope.ownerId === ownerId &&
      (entry.status === 'queued' || entry.status === 'inflight'),
  )
  return [...unsent, ...comments, ...creations].sort(
    (a, b) => a.envelope.enqueuedAt - b.envelope.enqueuedAt,
  )
}

/** Each reconnect owns the login/workspace that triggered it, including FIFO waits. */
export function resumeOfflineWrites(queryClient: QueryClient): Promise<void> {
  let resume = resumers.get(queryClient)
  if (!resume) {
    resume = createOwnedResume({
      capture: captureRequestScope,
      ownerId: () => useAuth.getState().user?.id ?? null,
      restore: restoreOfflineLedger,
      resumePaused: () => queryClient.resumePausedMutations(),
      queued: ownerId =>
        replayCandidates(ownerId, queryClient).map(entry => async () => {
          const current = offlineLedger.get(
            entry.envelope.idempotencyKey,
            ownerId,
          )
          if (
            current !== entry ||
            !replayCandidates(ownerId, queryClient).includes(current)
          )
            return
          if (
            current.envelope.kind === 'thread-comment.create' ||
            current.envelope.kind === 'thread.create'
          ) {
            const key = [...OFFLINE_MUTATION_KEYS[current.envelope.kind]]
            const defaults = queryClient.getMutationDefaults(key)
            if (!defaults.mutationFn)
              throw new Error('Comment recovery is unavailable')
            // Keep original attempts/body; this is server-deduped recovery, not
            // an explicit reset of a command that requires a user's decision.
            await queryClient
              .getMutationCache()
              .build(queryClient, { ...defaults, mutationKey: key })
              .execute(current.envelope)
          } else await retryOfflineWrite(queryClient, offlineLedger, current)
        }),
      prune: () => offlineLedger.prune(),
      reportFailure: () =>
        console.warn('[OfflineQueue] Replay finished with errors'),
    })
    resumers.set(queryClient, resume)
  }
  return resume()
}

/**
 * Wire everything up. Call once, as early as possible — the order matters:
 *
 *   1. mutation defaults, so restored mutations have a `mutationFn` to run
 *   2. the ledger, so a replay can recognise a write it already sent
 *   3. `onlineManager`, so TanStack pauses instead of failing
 *   4. only then resume
 */
export function initializeOfflineQueue(queryClient: QueryClient): () => void {
  registerOfflineMutationDefaults(queryClient)
  void restoreOfflineLedger().catch(() =>
    console.warn('[OfflineQueue] Restore could not be confirmed'),
  )

  onlineManager.setEventListener(setOnline => {
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)
    // A wake or a tab-return is the moment `navigator.onLine` is most likely to
    // have changed without an event having fired.
    const handleRecheck = () => setOnline(readBrowserOnline())

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    window.addEventListener('focus', handleRecheck)
    document.addEventListener('visibilitychange', handleRecheck)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('focus', handleRecheck)
      document.removeEventListener('visibilitychange', handleRecheck)
    }
  })

  onlineManager.setOnline(readBrowserOnline())

  // App start is a resume trigger in its own right: writes captured in a
  // previous session come back as paused mutations and would otherwise sit
  // there until the next connectivity *transition*, which never arrives if the
  // machine was already online at launch.
  if (onlineManager.isOnline()) {
    void resumeOfflineWrites(queryClient)
  }

  const unsubscribeOnline = onlineManager.subscribe(online => {
    if (online) void resumeOfflineWrites(queryClient)
  })

  // Startup can reconnect before portal bootstrap finishes. Resume only after
  // the exact registered account/entity source reader succeeds, never a cache
  // write, preview, unrelated reader or another account's retained proof.
  const unsubscribePortalReader = queryClient
    .getQueryCache()
    .subscribe(event => {
      if (
        event.type !== 'updated' ||
        event.action.type !== 'success' ||
        event.action.manual ||
        !onlineManager.isOnline()
      )
        return
      const entityId = getCurrentClientPortalContext()?.entityId
      if (!entityId) return
      const scope = getWorkspaceSessionSnapshot()
      if (
        event.query.queryHash !==
        hashKey(portalThreadSummaryKey(scope, entityId))
      )
        return
      const owner = selectedCommentOwner(queryClient)
      if (
        !owner ||
        !replayCandidates(owner.userId, queryClient).some(
          entry => entry.envelope.kind === 'thread-comment.create',
        )
      )
        return
      void resumeOfflineWrites(queryClient)
    })

  return () => {
    unsubscribeOnline()
    unsubscribePortalReader()
  }
}

/** Count of writes waiting or blocked — what the status chip shows. */
export function getPendingWriteCount(): number {
  return selectPendingWrites(
    selectOwnerWrites(
      offlineLedger.list(),
      useAuth.getState().user?.id ?? null,
    ),
  ).length
}
